import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import Button from 'components/field/Button';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { PRICE_CONFIGURATIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const defaultColSpan = {
	md: 6
};

const AddLockerDimensionsForm = NiceModal.create(({ id, modalName, mode }) => {
	// Hooks and states declaration
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();
	const { closeModal } = useModalRouter();

	// Validation schema definition
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					name: yup.string().nullable().required(),
					length: yup.number().min(0).max(9999).positive().nullable().required(),
					width: yup.number().min(0).max(9999).positive().nullable().required(),
					height: yup.number().min(0).max(9999).positive().nullable().required()
				})
				.required(),
		[]
	);
	const fillForm = useCallback(async () => {
		try {
			const defaultValues = {
				name: null,
				length: null,
				width: null,
				height: null
			};

			if (!id) return defaultValues;

			const response = await axiosPrivate.post(PRICE_CONFIGURATIONS.GET_DC_WORKING_HOURS, { id });
			const { status, data } = response;

			if (status === 200) {
				return { ...defaultValues, ...data };
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, [id]);

	// Form setup using react-hook-form
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;

	const isLoading = isFormLoading || isSubmitting;
	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const onSubmit = async (params) => {
		try {
			const response = await axiosPrivate.post(PRICE_CONFIGURATIONS.ADD_LOCKER_DIMENSIONS, params);

			const { status, data } = response;

			if (status === 200) {
				showSuccess();
				handleCloseModal();
			} else {
				AppLogger.error(data);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	};

	return (
		<Modal fullwidth name={modalName} maxWidth="small" className="modal-addLockerDimensions">
			<ModalTitle>
				<Typography variant="p1">addLockerDimensions</Typography>
			</ModalTitle>
			<ModalContent>
				<Form borderless formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="lockerSizeName" isLoading={isLoading} required>
						<StringInput name="name" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="length" isLoading={isLoading} required>
						<IntegerInput name="length" min={0} unit="cm" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="width" isLoading={isLoading} required>
						<IntegerInput name="width" min={0} unit="cm" />
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="height" isLoading={isLoading} required>
						<IntegerInput name="height" min={0} unit="cm" />
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					loading={isLoading}
					label={mode === 'edit' ? 'save' : 'button.add'}
					disabled={!isDirty}
					disabledTooltip={!isDirty && 'formInfo.noChanges'}
					onClick={handleSubmit(onSubmit, AppLogger.log)}
				/>
			</ModalActions>
		</Modal>
	);
});

export default AddLockerDimensionsForm;
